import { FC, memo, useCallback } from "react";
import isEqual from "lodash/isEqual";
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { TournamentOutput } from "~@/api/models";
import map from "lodash/map";

type TournamentSelectorProps = {
  tournaments: TournamentOutput[];
  tournamentId: number;
  onTournamentChange: (value: number) => void;
  backgroundColor?: "white" | "gray";
};

const TournamentSelector: FC<TournamentSelectorProps> = ({
  tournaments,
  tournamentId,
  onTournamentChange,
  backgroundColor = "white",
}) => {
  const renderTournaments = useCallback(() => {
    return map(tournaments, (tournament) => (
      <MenuItem key={tournament.id!} value={tournament.id!}>
        {tournament.title}
      </MenuItem>
    ));
  }, [tournaments]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<typeof tournamentId>) => {
      const value = event.target.value;

      onTournamentChange(typeof value === "string" ? parseInt(value) : value);
    },
    [onTournamentChange]
  );

  return (
    <FormControl fullWidth>
      <Select<typeof tournamentId>
        id="tournaments-select"
        value={tournamentId}
        onChange={handleChange}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 16 + 32 * 5,
            },
          },
        }}
        sx={{
          bgcolor: backgroundColor === "gray" ? "rgba(0, 0, 0, 0.03)" : "#FFFFFF",
          backgroundColor: backgroundColor === "gray" ? "rgba(0, 0, 0, 0.03)" : "#FFFFFF",
          "& .MuiSelect-select": {
            backgroundColor:
              backgroundColor === "gray"
                ? "rgba(0, 0, 0, 0.03) !important"
                : "#FFFFFF !important",
            color: "rgba(0, 0, 0, 0.85) !important",
          },
          "&:hover .MuiSelect-select": {
            backgroundColor:
              backgroundColor === "gray"
                ? "rgba(0, 0, 0, 0.03) !important"
                : "#FFFFFF !important",
          },
          "&[aria-expanded=true] .MuiSelect-select": {
            backgroundColor: "#D80E15 !important",
            color: "#FFFFFF",
          },
          "& .MuiSelect-icon": {
            color: "rgba(0, 0, 0, 0.85) !important",
          },
          "&.Mui-focused .MuiSelect-icon": {
            color: "rgba(0, 0, 0, 0.85) !important",
          },
          "&[aria-expanded=true] .MuiSelect-icon": {
            color: "#FFFFFF !important",
          },
        }}
      >
        {renderTournaments()}
      </Select>
    </FormControl>
  );
};

export default memo(TournamentSelector, isEqual);
